import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { NSInput } from 'bricks';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { NSInfoBanner } from 'components/NSInfoBanner/NSInfoBanner';
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import useToast from 'hooks/useToast';
import { useDeactivatedUserSupport } from '../../hooks';
import { ISignUpManager } from '../../useSignUpManager';
import { TermsAndConditionsBlock } from '../../../../../components/signup/TermsAndConditionsBlock';
import { SocialSignUpButtons } from '../../components';
import { SocialSignUpConnectionToLabel } from '../../constants';
import { REQUIRES_ADMIN_FOR_DEAL_ACCESS_MESSAGE } from '../../signUpValidationConstants';

interface IProps {
    onClickManualSignUp: () => void;
    signUpManager: ISignUpManager;
}

const SignUpMethodSection: React.FC<IProps> = ({ onClickManualSignUp, signUpManager }) => {
    const {
        socialSignUpEnabled,
        email,
        userOrgLookupData,
        socialSignUpConnectionType,
        handleEmailChange,
        handleSocialSignUp,
        resetSignUpState,
    } = signUpManager;

    const { mutate: sendDeactivatedUserSupport } = useDeactivatedUserSupport();
    const { showSuccess, showError } = useToast();
    const { loginWithRedirect } = useAuth0();

    const isUserDisabled = userOrgLookupData?.user?.isDisabled === true;
    const requiresAdminForDealAccess = Boolean(userOrgLookupData?.requiresAdminForDealAccess);

    const hasActiveUserAndOrg = userOrgLookupData?.user
        && !isUserDisabled
        && !requiresAdminForDealAccess
        && userOrgLookupData?.org
        && !email.errorMsg;

    const hasNoUserButExistingOrg = !userOrgLookupData?.user && userOrgLookupData?.org && !email.errorMsg;

    const getButtonText = () => {
        if (hasActiveUserAndOrg) {
            return 'Start using Northspyre Deal';
        } if (socialSignUpConnectionType) {
            return `Continue with my ${SocialSignUpConnectionToLabel[socialSignUpConnectionType]} account`;
        }
        // Default case: no existing user and no existing org, or no existing user but existing org
        return 'Continue with my email';
    };

    const isButtonDisabled = isUserDisabled || requiresAdminForDealAccess || !userOrgLookupData || !email.value || !!email.errorMsg;

    const handleContactSupport = () => {
        const userEmail = userOrgLookupData?.user?.email;

        if (!userEmail) {
            console.error('User email not available');
            return;
        }

        sendDeactivatedUserSupport(
            {
                user_email: userEmail,
            },
            {
                onSuccess: () => {
                    showSuccess('A customer support representative will be in touch with you via email soon.');
                },
                onError: error => {
                    showError('Failed to send support request. Please try again or email concierge@northspyre.com directly.');
                },
            },
        );
    };

    const deactivatedAccountMessage = isUserDisabled ? (
        <span>
            This is a deactivated account. An admin will need to restore your access.
            {' '}
            <button
                type="button"
                onClick={handleContactSupport}
                className="text-primary bg-transparent border-0 p-0 font-inherit"
            >
                Contact support
            </button>
            .
        </span>
    ) : '';

    const handleButtonClick = () => {
        if (hasActiveUserAndOrg) {
            loginWithRedirect({
                authorizationParams: {
                    login_hint: email.value,
                },
            });
        } else {
            onClickManualSignUp();
        }
    };

    return (
        <>
            <div className="DirectSignUp__FormFields mb-1">
                <div className="DirectSignUp__InputWrapper">
                    <label htmlFor="email" className="DirectSignUp__SectionLabel">Email</label>
                    <NSInput
                        id="email"
                        name="email"
                        type="text"
                        placeholder="Get started with your work email"
                        value={email.value}
                        onChange={handleEmailChange}
                        customClassName="DirectSignUp__Input"
                        autoComplete="email"
                        invalid={!!email.errorMsg}
                        showErrorMsg={!!email.errorMsg}
                        errorMsg={email.errorMsg}
                        disabled={socialSignUpConnectionType !== undefined}
                        hideInvalidIcon
                    />
                </div>
                {isUserDisabled && (
                    <NSInfoBanner
                        title={deactivatedAccountMessage as any}
                        className="mt-2 NSCard__left-border--danger"
                        boldTitle={false}
                        showIcon={false}
                    />
                )}
                {requiresAdminForDealAccess && !isUserDisabled && (
                    <NSInfoBanner
                        title={REQUIRES_ADMIN_FOR_DEAL_ACCESS_MESSAGE}
                        className="mt-2 NSCard__left-border--warning"
                        customIcon={faExclamationTriangle}
                        iconSize="sm"
                        boldTitle={false}
                        warning
                    />
                )}
                {hasNoUserButExistingOrg && (
                    <NSInfoBanner
                        title="Your organization already uses Northspyre."
                        className="mt-2 NSCard__left-border--info"
                        customIcon={faInfoCircle}
                        iconSize="sm"
                        boldTitle={false}
                    />
                )}
                {hasActiveUserAndOrg && (
                    <NSInfoBanner
                        title="You already use Northspyre. We've added access to Northspyre Deal."
                        className="mt-2 NSCard__left-border--info"
                        customIcon={faInfoCircle}
                        iconSize="sm"
                        boldTitle={false}
                    />
                )}
            </div>
            <TermsAndConditionsBlock />
            <div className="DirectSignUp__FullWidth d-flex gap-2">
                <NSButtonBase
                    outline
                    color="primary"
                    callback={handleButtonClick}
                    className="mt-2 DirectSignUp__Button heap-deal-signup-direct-create-account-with-email flex-grow-1"
                    disabled={isButtonDisabled}
                    dataTestId="createAccountButton"
                >
                    {getButtonText()}
                </NSButtonBase>
                {socialSignUpConnectionType && (
                    <div className="w-100 d-flex justify-content-center">
                        <NSButtonBase
                            callback={resetSignUpState || (() => undefined)}
                            className="DirectSignUp__LinkButton"
                            disabled={!resetSignUpState}
                            dataTestId="resetSignUpButton"
                        >
                            Change sign up method
                        </NSButtonBase>
                    </div>
                )}
            </div>
            <div className="DirectSignUp__Divider">
                <hr className="DirectSignUp__DividerLine" />
                <span className="DirectSignUp__DividerText">OR</span>
                <hr className="DirectSignUp__DividerLine" />
            </div>
            <div className="DirectSignUp__SocialSignUpButtonContainer">
                <SocialSignUpButtons
                    handleSocialSignUp={handleSocialSignUp}
                    enabled={socialSignUpEnabled && !requiresAdminForDealAccess}
                />
            </div>
        </>
    );
};

export default SignUpMethodSection;
